/**
 * Runtime configuration from `/config.json` (see docs/runtime-config.md).
 *
 * DOM binding for createRuntimeConfigLoader (runtime-config-utils.ts, where the logic and
 * its tests live). src/index.tsx awaits loadRuntimeConfig() before dynamically importing
 * the app, so every module sees the resolved values. Absent keys fall back to the
 * build-time VITE_* value.
 */

import {
    createRuntimeConfigLoader,
    resolveRuntimeConfigUrl,
    type RuntimeConfigKey,
} from "./runtime-config-utils";

let loader: ReturnType<typeof createRuntimeConfigLoader> | null = null;

/** Fetch /config.json once. Never rejects: any failure boots with the baked values. */
export async function loadRuntimeConfig(): Promise<void> {
    loader ??= createRuntimeConfigLoader(
        (url, init) => fetch(url, init),
        resolveRuntimeConfigUrl(import.meta.env.BASE_URL, document.baseURI),
    );
    await loader.load();
    const error = loader.getError();
    if (error) {
        console.warn(
            "[runtime-config] /config.json not used, booting with build-time values:",
            error.message,
        );
    }
}

/** Why /config.json was not used, or null (loaded, or simply absent). For Sentry. */
export function getRuntimeConfigError(): Error | null {
    return loader?.getError() ?? null;
}

/** Runtime value for `key`, else `fallback` (the build-time value). */
export function getRuntimeConfigValue(key: RuntimeConfigKey, fallback: string): string {
    return loader?.get(key, fallback) ?? fallback;
}
